/**
 * Customer Guard for SnapHub PWA.
 *
 * Ensures that employees and admins have selected a customer before
 * they can access most protected pages (scanner, cart, manual entry, etc.).
 *
 * Exceptions:
 * - /customers page → always accessible (so employees can add customers)
 * - /login page → always accessible
 * - /imprint page → always accessible
 *
 * For users with role `customer`, this guard always passes because their
 * customer number is automatically derived from the JWT token.
 */

import { storageAdapter } from './storage-adapter';
import { decodeTokenPayload, resolveRoleFromPayload } from '../features/auth/auth.service';
import { CUSTOMER_NUMBER_KEY } from '../features/order/user.service';

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

/** Routes that are exempt from the customer selection requirement. */
const EXEMPT_ROUTES: string[] = ['/customers', '/login', '/imprint'];

/** Routes that are exempt from the customerId-pending check. */
const CUSTOMERID_EXEMPT_ROUTES: string[] = ['/login', '/imprint', '/help', '/help/article', '/settings', '/change-password'];

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Returns `true` when the current route requires a customer to be selected
 * but none has been chosen yet.
 *
 * @param currentRoute The current hash route (e.g. '/', '/scanner')
 */
export function requiresCustomerSelection(currentRoute: string): boolean {
  // Exempt routes never require customer selection
  if (EXEMPT_ROUTES.includes(currentRoute)) return false;

  // Not authenticated → auth guard handles this, not us
  const token = storageAdapter.getAccessToken();
  if (!token || token.trim().length === 0) return false;

  // Decode role from token
  const payload = decodeTokenPayload(token);
  if (!payload) return false;

  const role = resolveRoleFromPayload(payload);

  // Customers always have their customerId from the token → no selection needed
  if (role === 'customer') return false;

  // Employees and admins need a manually selected customer
  if (role === 'employee' || role === 'admin') {
    const selectedCustomer = sessionStorage.getItem(CUSTOMER_NUMBER_KEY);
    return !selectedCustomer || selectedCustomer.trim().length === 0;
  }

  return false;
}

// ---------------------------------------------------------------------------
// CustomerId Pending Guard
// ---------------------------------------------------------------------------

/**
 * Returns `true` when the authenticated user has no customerId assigned.
 * This happens when a user logs in via SSO (Meesenburg ID) but hasn't been
 * assigned a customer number yet by an admin.
 *
 * Pages that require a customerId (cart, scanner, etc.) should show a
 * pending overlay instead of loading.
 *
 * @param currentRoute The current hash route (e.g. '/', '/scanner')
 */
export function requiresCustomerId(currentRoute: string): boolean {
  // Exempt routes are always accessible without a customerId
  if (CUSTOMERID_EXEMPT_ROUTES.includes(currentRoute)) return false;

  // Not authenticated → auth guard handles this
  const token = storageAdapter.getAccessToken();
  if (!token || token.trim().length === 0) return false;

  const payload = decodeTokenPayload(token);
  if (!payload) return false;

  const role = resolveRoleFromPayload(payload);

  // Employees and admins select customers manually — they don't need a customerId in the token
  if (role === 'employee' || role === 'admin') return false;

  // For customers: check if customerId is present in the token or user object
  const customerId = payload.customerId || payload.customer_id || '';
  return !customerId || customerId.trim().length === 0;
}
